/**
 * DV-STAGE-003: Ring Buffer Implementation
 * Memory-efficient circular buffer for logs with performance optimizations
 */

export interface RingBufferOptions {
  maxSize: number;
  onEviction?: (item: any) => void;
  onSizeChange?: (size: number, maxSize: number) => void;
}

export interface RingBufferStats {
  size: number;
  maxSize: number;
  totalAdded: number;
  totalEvicted: number;
  memoryUsageKB: number;
  utilizationPercent: number;
}

export class RingBuffer<T> {
  private buffer: (T | undefined)[];
  private head: number = 0; // Points to the next insertion point
  private tail: number = 0; // Points to the oldest item
  private currentSize: number = 0;
  private totalAdded: number = 0;
  private totalEvicted: number = 0;
  private readonly maxSize: number;
  private readonly onEviction?: (item: T) => void;
  private readonly onSizeChange?: (size: number, maxSize: number) => void;

  constructor(options: RingBufferOptions) {
    this.maxSize = options.maxSize;
    this.onEviction = options.onEviction;
    this.onSizeChange = options.onSizeChange;
    this.buffer = new Array(this.maxSize);
  }

  /**
   * Add an item to the buffer
   */
  add(item: T): void {
    const evictedItem = this.buffer[this.head];

    // If buffer is full, evict the oldest item
    if (this.currentSize === this.maxSize && evictedItem !== undefined) {
      this.onEviction?.(evictedItem);
      this.totalEvicted++;
      this.tail = (this.tail + 1) % this.maxSize;
    } else if (this.currentSize < this.maxSize) {
      this.currentSize++;
    }

    // Add new item at head
    this.buffer[this.head] = item;
    this.head = (this.head + 1) % this.maxSize;
    this.totalAdded++;

    this.onSizeChange?.(this.currentSize, this.maxSize);
  }

  /**
   * Get all items in insertion order (oldest to newest)
   */
  getAll(): T[] {
    if (this.currentSize === 0) {
      return [];
    }

    const result: T[] = [];
    let index = this.tail;

    for (let i = 0; i < this.currentSize; i++) {
      const item = this.buffer[index];
      if (item !== undefined) {
        result.push(item);
      }
      index = (index + 1) % this.maxSize;
    }

    return result;
  }

  /**
   * Get the latest N items (newest to oldest)
   */
  getLatest(count: number): T[] {
    const all = this.getAll();
    return all.slice(-count).reverse();
  }

  /**
   * Get items matching a predicate
   */
  filter(predicate: (item: T) => boolean): T[] {
    return this.getAll().filter(predicate);
  }

  /**
   * Clear all items
   */
  clear(): void {
    // Evict all items before clearing
    const items = this.getAll();
    items.forEach(item => this.onEviction?.(item));

    this.buffer.fill(undefined);
    this.head = 0;
    this.tail = 0;
    this.totalEvicted += this.currentSize;
    this.currentSize = 0;

    this.onSizeChange?.(0, this.maxSize);
  }

  /**
   * Get buffer statistics
   */
  getStats(): RingBufferStats {
    // Estimate memory usage (rough calculation)
    const sampleItem = this.currentSize > 0 ? this.buffer[this.tail] : null;
    const itemSizeEstimate = sampleItem ? JSON.stringify(sampleItem).length * 2 : 100; // UTF-16 bytes
    const memoryUsageKB = (this.currentSize * itemSizeEstimate) / 1024;

    return {
      size: this.currentSize,
      maxSize: this.maxSize,
      totalAdded: this.totalAdded,
      totalEvicted: this.totalEvicted,
      memoryUsageKB: Math.round(memoryUsageKB * 100) / 100,
      utilizationPercent: Math.round((this.currentSize / this.maxSize) * 100)
    };
  }

  /**
   * Get current size
   */
  get size(): number {
    return this.currentSize;
  }

  /**
   * Check if buffer is full
   */
  get isFull(): boolean {
    return this.currentSize === this.maxSize;
  }

  /**
   * Check if buffer is empty
   */
  get isEmpty(): boolean {
    return this.currentSize === 0;
  }

  /**
   * Get the oldest item without removing it
   */
  peek(): T | undefined {
    if (this.currentSize === 0) {
      return undefined;
    }
    return this.buffer[this.tail];
  }

  /**
   * Get the newest item without removing it
   */
  peekLatest(): T | undefined {
    if (this.currentSize === 0) {
      return undefined;
    }
    const latestIndex = (this.head - 1 + this.maxSize) % this.maxSize;
    return this.buffer[latestIndex];
  }

  /**
   * Compact buffer to remove undefined holes (maintenance operation)
   */
  compact(): void {
    const items = this.getAll();
    this.buffer.fill(undefined);
    this.head = 0;
    this.tail = 0;
    this.currentSize = 0;

    items.forEach(item => this.add(item));
  }
}

/**
 * Create a ring buffer for log entries with default settings
 */
export function createLogRingBuffer(maxEntries: number = 1000) {
  return new RingBuffer<any>({
    maxSize: maxEntries,
    onEviction: (logEntry) => {
      // Optional: Could send evicted logs to persistent storage
      if (process.env.NODE_ENV === 'development') {
        console.debug('Log entry evicted from ring buffer:', logEntry.id || logEntry.ts);
      }
    },
    onSizeChange: (size, maxSize) => {
      // Optional: Could emit metrics about buffer utilization
      if (size === maxSize && process.env.NODE_ENV === 'development') {
        console.warn('Log ring buffer is at capacity');
      }
    }
  });
}

/**
 * Utility to export ring buffer contents for download
 */
export function exportRingBufferLogs<T>(
  buffer: RingBuffer<T>,
  formatter: (items: T[]) => string = JSON.stringify
): { content: string; size: number; stats: RingBufferStats } {
  const items = buffer.getAll();
  const content = formatter(items);
  const stats = buffer.getStats();

  return {
    content,
    size: content.length,
    stats
  };
}

// Type exports handled inline above